import { paycheckNewLineFragments } from '@/queries/paycheckNewFragments';

import {
  GQL_paycheckNew_newPaycheck,
  GQL_paycheckNew_newPaycheck_paycheckLines,
} from '@/queries/__generated__/paycheckNew';
import {
  GQL_PaycheckCreate, GQL_PaycheckUpdate,
  GQL_PaycheckLineCreate, GQL_PaycheckLineUpdate,
} from '@/queries/global';
import {
  GQL_paycheckForm_paycheck,
  GQL_paycheckForm_paycheck_paycheckLines,
} from '@/queries/__generated__/paycheckForm';
import {
  GQL_findTaxDeductionVoluntary, GQL_findTaxDeductionVoluntaryVariables,
} from '@/queries/__generated__/findTaxDeductionVoluntary';

import gql from 'graphql-tag';
import { apolloProvider } from '@/utils/vue-apollo';
import Decimal from '@/utils/decimal';
import { generateUID } from '@/utils/utils';
const client = apolloProvider.defaultClient;


// Paycheck form is either
// - data from new paycheck query combined with data to post to create
// - paycheck from database fetcged with fragment paycheckFormFragment combined
//   with data for update

export type PaycheckForm =
  GQL_paycheckNew_newPaycheck & GQL_PaycheckCreate |
  GQL_paycheckForm_paycheck & GQL_PaycheckUpdate;


export type PaycheckFormLine =
  (GQL_paycheckNew_newPaycheck_paycheckLines & GQL_PaycheckLineCreate) |
  (GQL_paycheckForm_paycheck_paycheckLines & GQL_PaycheckLineUpdate)

interface ILitePaycheckLine {
  taxDeduction: boolean;
  taxDeductionVoluntary: boolean;
  description: string;
}

export function lineIsTaxDeductionOrdinary(line: ILitePaycheckLine): boolean {
  return line.taxDeduction && !line.taxDeductionVoluntary;
}
export function lineIsTaxDeductionVoluntary(line: ILitePaycheckLine): boolean {
  return line.taxDeduction && line.taxDeductionVoluntary;
}

export function lineIsTaxDeductionTaxTable(line: ILitePaycheckLine): boolean {
  return lineIsTaxDeductionOrdinary(line) && line.description === 'Skattetrekk tabell';
}

export function lineIsTaxDeductionTaxPercent(line: ILitePaycheckLine): boolean {
  return lineIsTaxDeductionOrdinary(line) && line.description === 'Skattetrekk prosent';
}

function findNotDeletedLines(paycheckForm: PaycheckForm) {
  return (paycheckForm.paycheckLines as PaycheckFormLine[])
    .filter((line) => !(line as unknown as { _destroy: boolean })._destroy);
}

async function fetchTaxDeductionVoluntaryLine(workRelationId: string) {
  const query = gql`
    query findTaxDeductionVoluntary($work_relation_id: ID!) {
      paycheckLineTaxDeductionVoluntaryNew(workRelationId: $work_relation_id) {
        paycheckLine {
          ...paycheckNewLineFragments
        }
      }
    }
    ${paycheckNewLineFragments}
  `;

  return client
    .query<GQL_findTaxDeductionVoluntary, GQL_findTaxDeductionVoluntaryVariables>({
      query,
      fetchPolicy: 'no-cache',
      variables: {work_relation_id: workRelationId},
    })
    .then(({data}) => data.paycheckLineTaxDeductionVoluntaryNew.paycheckLine);
}

export function findTaxDeductionLine(paycheckForm: PaycheckForm) {
  const notDeletedLines = findNotDeletedLines(paycheckForm);

  if (paycheckForm.taxDeductionMethod === 'MAIN_EMPLOYER_TABLE') {
    return notDeletedLines.find(lineIsTaxDeductionTaxTable);
  } else if (paycheckForm.taxDeductionMethod === 'MAIN_EMPLOYER_PERCENTAGE' ||
             paycheckForm.taxDeductionMethod === 'SECONDARY_EMPLOYER_PERCENTAGE') {
    return notDeletedLines.find(lineIsTaxDeductionTaxPercent);
  } else if (paycheckForm.taxDeductionMethod === 'TAX_EXEMPTION') {
    return notDeletedLines.find(lineIsTaxDeductionTaxPercent);
  }
}

function findTaxDeductionVoluntaryLine(paycheckForm: PaycheckForm) {
  const notDeletedLines = findNotDeletedLines(paycheckForm);

  return notDeletedLines.find(lineIsTaxDeductionVoluntary);
}

function adjustTaxDeductionVolunary(paycheckForm: PaycheckForm, taxDeduction: Decimal, voluntary: Decimal) {
  (paycheckForm.paycheckLines as PaycheckFormLine[])
    .filter(line => lineIsTaxDeductionOrdinary(line) || lineIsTaxDeductionVoluntary(line))
    .forEach(line => {
      line.quantity = new Decimal(0);
      line.rate = new Decimal(0);
      line.sum = new Decimal(0);
    });
  const taxDeductionLine = findTaxDeductionLine(paycheckForm);
  const taxDeductionVoluntaryLine = findTaxDeductionVoluntaryLine(paycheckForm);

  if (taxDeductionLine && taxDeductionVoluntaryLine) {
    taxDeductionLine.sum = taxDeduction;
    taxDeductionVoluntaryLine.sum = voluntary;
  }
}

export function moveExcessTaxDeductiontoTaxDeductionVolunary(paycheckForm: PaycheckForm, taxDeductionToMove: Decimal) {
  const notDeletedLines = findNotDeletedLines(paycheckForm);

  const ordinaryTaxDeduction: Decimal = notDeletedLines
      .filter(line => lineIsTaxDeductionOrdinary(line))
      .reduce(((acc, line) => acc.plus(line.sum)), new Decimal(0));

  const voluntaryTaxDeduction: Decimal = notDeletedLines
    .filter(lineIsTaxDeductionVoluntary)
    .reduce(((acc, line) => acc.plus(line.sum)), new Decimal(0));

  const taxDeductionVoluntaryLine: PaycheckFormLine | undefined =
    notDeletedLines
      .find(lineIsTaxDeductionVoluntary);

  if (taxDeductionVoluntaryLine) {
    adjustTaxDeductionVolunary(
      paycheckForm,
      ordinaryTaxDeduction.minus(taxDeductionToMove),
      voluntaryTaxDeduction.add(taxDeductionToMove),
    );
    return Promise.resolve();
  } else {
    return fetchTaxDeductionVoluntaryLine(paycheckForm.workRelation.id)
      .then(line => {
        const prepedLine = {
          ...line,
          paycheckLineTemplateId: line.paycheckLineTemplate?.id || '',
          carId: line.car?.id || '',
          projectId: line.project?.id || '',
          departmentId: line.department?.id || '',
          $_UID: generateUID(),
        } as PaycheckFormLine;
        (paycheckForm.paycheckLines as PaycheckFormLine[])
          .push(prepedLine);

        adjustTaxDeductionVolunary(
          paycheckForm,
          ordinaryTaxDeduction.minus(taxDeductionToMove),
          voluntaryTaxDeduction.add(taxDeductionToMove),
        );
      });
  }
}
